import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useEffect, useRef, useState } from 'react';
import HeroHeader from './components/HeroHeader';
import InstallationGuide from './components/InstallationGuide';
import SaveCopyDialog from './components/SaveCopyDialog';

import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { redeemOffer } from 'src/app/services/PlanService';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useSearchParams } from 'react-router-dom';

interface Offer {
  date: string;
  plan_title: string;
  service: string;
  coverage: string;
  cardNumber: string;
  validity: string;
  esimQr: string;
}

interface RedeemResponse {
  status: 'pending' | 'failed' | 'success';
  date?: string;
  plan_title?: string;
  service?: string;
  coverage?: string;
  validity?: string;
  cardNumber?: string;
  esimQr?: string;
}


const RedeemOffer = () => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [showDialog, setShowDialog] = useState(false);
  const [offerData, setOfferData] = useState<Offer | null>(null);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callCountRef = useRef(0);

  const USE_MOCK = true;
  const [searchParams] = useSearchParams();
  const redemptionId = searchParams.get("redemption_id");

  // -------------------
  // Processing UI
  // -------------------
  const ProcessingUI = () => (
    <div className="w-full py-8 flex justify-content-center">
      <div className="col-12 sm:col-10 md:col-8 lg:col-6 px-4 text-center">
        <i className="pi pi-stopwatch" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
        <p className="mt-4 text-5xl font-bold">
          {t('redeem_offer.processing_request_title')}
        </p>
        <p className="text-sm text-500 mt-2">
          {t('redeem_offer.processing_request_description')}
        </p>
        <p className="text-sm text-500 mt-2">
          {t('redeem_offer.processing_request_note')}
        </p>
        <i className="pi pi-spin pi-cog" style={{ fontSize: '5rem', color: '#f59e0b' }}></i>
      </div>
    </div>
  );

  // -------------------
  // Mock API
  // -------------------
  const mockRedeemOffer = async (last4: number) => {
    callCountRef.current++;
    console.log('Mock API called, call #', callCountRef.current);

    return new Promise<AxiosResponse<RedeemResponse>>((resolve) => {
      setTimeout(() => {
        if (callCountRef.current <= 2) {
          resolve({
            status: 200,
            data: { status: 'pending' },
            statusText: 'OK',
            headers: {},
            config: {} as any,
          });
          return;
        }

        if (callCountRef.current === 3) {
          resolve({
            status: 200,
            data: { status: 'failed' },
            statusText: 'OK',
            headers: {},
            config: {} as any,
          });
          return;
        }

        resolve({
          status: 200,
          data: {
            status: 'success',
            date: '2026-01-25 23:11:00',
            plan_title: '1GB Global Data',
            service: 'Data-only',
            coverage: 'Valid globally',
            validity: '7 Days',
            cardNumber: '**** 3526',
            esimQr: 'https://www.flexiroam.com/en-us',
          },
          statusText: 'OK',
          headers: {},
          config: {} as any,
        });
      }, 1000);
    });
  };


  useEffect(() => {
    console.log('Redemption Id ', redemptionId);
    
    // 1. Check if offerData is stored in cookies
    const storedData = Cookies.get('offerData');
    if (storedData) {
      try {
        setOfferData(JSON.parse(storedData));
        return; // Skip polling if data exists
      } catch (err) {
        console.error('Failed to parse stored offerData:', err);
      }
    }

    // -------------------
    // Polling logic (only runs if no stored data)
    // -------------------
    let isFetching = false;

    const fetchOffer = async () => {
      if (isFetching) return;
      isFetching = true;

      try {
        const response = USE_MOCK
          ? (await mockRedeemOffer(1234)) as AxiosResponse<RedeemResponse>
          : (await redeemOffer(redemptionId)) as AxiosResponse<RedeemResponse>;

        if (response.status === 200) {
          const data = response.data;

          if (data.status === 'pending') {
            console.log('Pending...');
            return;
          }

          if (data.status === 'failed') {
            console.log('Temporary failure, keep polling...');
            toast.current?.show({
              severity: 'error',
              summary: 'Failed',
              detail: 'Offer redemption failed. Retrying...',
              life: 2000,
            });
            return;
          }

          if (data.status === 'success') {
            console.log('Success!');
            setOfferData(data as Offer);

            Cookies.set('offerData', JSON.stringify(data), {
              expires: 1 / 288, // 5 minutes
            });

            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
          }
        }
      } catch (error) {
        console.error(error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong while fetching data.',
          life: 3000,
        });
      } finally {
        isFetching = false;
      }
    };

    fetchOffer();

    intervalRef.current = setInterval(fetchOffer, 2000); // every 2 sec
    timeoutRef.current = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      toast.current?.show({
        severity: 'warn',
        summary: 'Timeout',
        detail: 'Still processing. Please try again later.',
        life: 3000,
      });
    }, 20000); // stop after 20 sec

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // -------------------
  // Render
  // -------------------
  if (!offerData) {
    return <ProcessingUI />;
  }

  return (
    <>
      <Toast ref={toast} />
      <HeroHeader />

      <div className="w-full py-8">
        <div className="grid justify-content-center">
          <div className="col-12 sm:col-10 md:col-8 lg:col-6 px-4">
            <div className="text-center mb-6">
              <i className="pi pi-check-circle" style={{ color: '#22C55E', fontSize: '3.5rem' }}></i>
              <p className="mt-4 text-3xl font-bold">{t('redeem_offer.title')}</p>
              <p className="text-sm text-500 mt-2">{t('redeem_offer.description')}</p>
            </div>

            <Card className="border-round-3xl px-3 mb-3" style={{ backgroundColor: '#F8F7F5' }}>
              <p className="text-xl font-semibold mb-1">{t('redeem_offer.details')}</p>
              <p className="text-xs text-500 mb-4">{offerData.date}</p>

              <ul className="list-none p-0 m-0">
                <li className="flex justify-content-between py-3 border-bottom-1 surface-border">
                  <span>{t('redeem_offer.data_plan')}</span>
                  <span>{offerData.plan_title}</span>
                </li>
                <li className="flex justify-content-between py-3 border-bottom-1 surface-border">
                  <span>{t('redeem_offer.service')}</span>
                  <span>{offerData.service}</span>
                </li>
                <li className="flex justify-content-between py-3 border-bottom-1 surface-border">
                  <span>{t('redeem_offer.coverage')}</span>
                  <span>{offerData.coverage}</span>
                </li>
                <li className="flex justify-content-between py-3 border-bottom-1 surface-border">
                  <span>{t('redeem_offer.validity')}</span>
                  <span>{offerData.validity}</span>
                </li>
                <li className="flex justify-content-between py-3">
                  <span>{t('redeem_offer.card_last_4')}</span>
                  <span>{offerData.cardNumber}</span>
                </li>
              </ul>
            </Card>

            <InstallationGuide data={offerData} />

            <div className="flex justify-content-center mt-4">
              <Button onClick={() => setShowDialog(true)}>{t('redeem_offer.email_me_copy')}</Button>
            </div>
          </div>
        </div>
      </div>

      <SaveCopyDialog
        visible={showDialog}
        onClose={() => setShowDialog(false)}
        header={t('redeem_offer.success')}
        redemptionId={redemptionId || ''}
      />
    </>
  );
};

export default RedeemOffer;